import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, Wrench, Moon } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { core } from "@/lib/mh";
import { errText } from "@/lib/http";
import { Panel, Eyebrow, timeAgo } from "@/components/mh/bits";
import { Btn } from "@/components/mh/controls";
import { ToolFixGuide } from "@/components/mh/ToolFixGuide";

const STATE = {
  verified: { t: "Verified", c: "#D4FF3A" },
  missing: { t: "Missing", c: "#FF3B5C" },
  error: { t: "Error", c: "#FF3B5C" },
  declared: { t: "Declared", c: "#9A9AAE" },
  not_set: { t: "Not set", c: "#3A3A46" },
};
const CHECK_TEXT = { running: "Checking on OpenClaw…", done: "Last verified", failed: "Check failed", no_receipt: "No results came back" };
const tid = (n) => n.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const ToolRow = ({ t, canEdit, onToggle, onFix }) => {
  const s = STATE[t.state] || STATE.not_set;
  const bad = ["missing", "error"].includes(t.state);
  return (
    <label className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg border bg-white/[0.02] cursor-pointer hover:border-white/20 transition-colors"
      style={{ borderColor: t.state === "verified" ? "#D4FF3A33" : bad ? "#FF3B5C33" : "rgba(255,255,255,0.07)" }}>
      <input type="checkbox" checked={t.connected} disabled={!canEdit} onChange={(e) => onToggle(t.name, e.target.checked)} className="accent-[#D4FF3A] w-4 h-4 flex-shrink-0" data-testid={`tool-${tid(t.name)}`} />
      <span className="flex-1 min-w-0">
        <span className="text-sm block truncate">{t.name}</span>
        {t.broke_since && <span className="font-mono text-[10px] text-[#FF3B5C] block" data-testid={`tool-broke-${tid(t.name)}`}>broke {timeAgo(t.broke_since)}</span>}
        {t.note && <span className={`font-mono text-[10px] block truncate ${t.state === "verified" ? "text-muted-foreground" : "text-[#FF3B5C]"}`} title={t.note}>{t.note}</span>}
      </span>
      {t.state !== "verified" && (
        <button onClick={(e) => { e.preventDefault(); onFix(t); }} data-testid={`tool-fix-${tid(t.name)}`}
          className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full border text-[11px] flex-shrink-0 transition-colors ${bad ? "border-[#FF3B5C]/50 text-[#FF3B5C] hover:bg-[#FF3B5C]/10" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>
          <Wrench className="w-3 h-3" />{bad ? "Fix" : "Setup"}
        </button>
      )}
      <span className="font-mono text-[10px] uppercase tracking-wider flex-shrink-0 w-16 text-right" style={{ color: s.c }} data-testid={`tool-state-${tid(t.name)}`}
        title={t.checked_at ? `checked ${timeAgo(t.checked_at)}` : "never checked on OpenClaw"}>{s.t}</span>
    </label>
  );
};

const Nightly = ({ data, canVerify }) => {
  const [on, setOn] = useState(data.tool_check_nightly);
  const flip = async (v) => { setOn(v); try { await core.setNightly(v); toast.success(v ? "Nightly tool check on" : "Nightly tool check off"); } catch (e) { setOn(!v); toast.error(errText(e)); } };
  return (
    <label className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="nightly-toggle-wrap">
      <Moon className="w-3.5 h-3.5" /><Switch checked={on} disabled={!canVerify} onCheckedChange={flip} data-testid="nightly-toggle" />Re-check every night (03:00 UTC)
    </label>
  );
};

export const ToolsCard = ({ data, canEdit, canVerify, onToggle }) => {
  const [busy, setBusy] = useState(false);
  const [fix, setFix] = useState(null);
  const own = data.runtime.mode === "own" && !!data.runtime.base_url;
  const c = data.tool_check;
  const count = (st) => data.tools.filter((t) => t.state === st).length;
  const broken = data.tools.filter((t) => t.broke_since);
  const verify = async () => {
    setBusy(true);
    try { await core.verifyTools(); toast.success("Asked your OpenClaw agent to check every tool (read-only)"); } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  return (
    <Panel cls="p-5 sm:p-6 space-y-4" testId="tools-card">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <Eyebrow>Business tools</Eyebrow>
          <h3 className="font-display text-3xl mt-1">The apps your agents expect</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Tool logins live inside the client's OpenClaw, and Manahouse never holds them. Press Verify and the OpenClaw agent makes one harmless read-only call per tool, then reports back what it can reach.</p>
        </div>
        {canVerify && (
          <Btn variant="primary" onClick={verify} disabled={!own || busy || c?.status === "running"} testId="verify-tools-btn">
            {c?.status === "running" ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Verify on OpenClaw
          </Btn>
        )}
      </div>
      {!own && <p className="text-xs text-muted-foreground" data-testid="verify-needs-runtime">Verification runs on the client's own OpenClaw. On the managed runtime, tick the tools you've set up.</p>}
      {own && <Nightly key={String(data.tool_check_nightly)} data={data} canVerify={canVerify} />}
      {broken.length > 0 && (
        <div className="rounded-lg border border-[#FF3B5C]/40 bg-[#FF3B5C]/[0.06] p-3 text-sm" data-testid="tools-broke-alert">
          <span className="text-[#FF3B5C] font-medium">{broken.length} tool{broken.length === 1 ? "" : "s"} broke</span> since the last good check: {broken.map((t) => t.name).join(", ")}. Agents that rely on them may fail until they're fixed.
        </div>
      )}
      {c && (
        <div className="font-mono text-[11px] flex flex-wrap gap-x-4 gap-y-1" data-testid="tool-check-status">
          <span style={{ color: c.status === "done" ? "#D4FF3A" : c.status === "running" ? "#00E5FF" : "#FF3B5C" }}>{CHECK_TEXT[c.status] || c.status}</span>
          <span className="text-muted-foreground">{c.source === "nightly" ? "nightly · " : ""}agent {c.agentId} · {timeAgo(c.finished_at || c.requested_at)}</span>
          {c.error && <span className="text-[#FF3B5C]" data-testid="tool-check-error">{c.error}</span>}
        </div>
      )}
      <div className="flex flex-wrap gap-3 font-mono text-[11px]" data-testid="tool-counts">
        {["verified", "missing", "error", "declared", "not_set"].map((st) => <span key={st} style={{ color: STATE[st].c === "#3A3A46" ? "#6B6B7B" : STATE[st].c }}>{count(st)} {STATE[st].t.toLowerCase()}</span>)}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {data.tools.map((t) => <ToolRow key={t.name} t={t} canEdit={canEdit} onToggle={onToggle} onFix={setFix} />)}
      </div>
      <p className="text-[11px] text-muted-foreground">Ticking a box by hand marks the tool "declared" (not yet verified). Use Fix or Setup on any tool for step-by-step OpenClaw instructions.</p>
      <ToolFixGuide tool={fix} onClose={() => setFix(null)} onVerify={verify} canVerify={canVerify && own} />
    </Panel>
  );
};
